import React, { useRef, useEffect, useState } from "react";
import {
  HotelShopRoomTypePickerRedesign,
  HotelShopRoomTypePickerEnum,
  HotelShopRoomTypePickerVariant,
} from "halifax";
import { RouteComponentProps } from "react-router";
import clsx from "clsx";
import {
  SELECT_PC_ROOM,
  RoomInfoProducts,
  VIEWED_PAYMENT_SCHEDULE_MODAL,
  PAYMENT_SCHEDULE_SELECTED,
} from "redmond";
import { RoomPaymentSchedule } from "redmond/hotels-module/interfaces";
import { DesktopRoomPickerConnectorProps } from "./container";
import "./styles.scss";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";

import { HotelRoomPaymentScheduleModal } from "../HotelRoomPaymentScheduleModal";

export interface DesktopRoomPickerProps
  extends DesktopRoomPickerConnectorProps,
    RouteComponentProps {
  roomInfoProductsType?: HotelShopRoomTypePickerVariant;
  handleReadyToRedirect?: () => void;
  nonRefundablePolicyOverrideText?: string;
  isFintechHotelUpdatedUxEnabled?: boolean;
}

export const DesktopRoomPicker = (props: DesktopRoomPickerProps) => {
  const {
    checkinDate,
    checkoutDate,
    history,
    fetchPremierCollectionShop,
    adultsCount,
    childrenCount,
    roomInfoProductsType,
    handleReadyToRedirect,
    isHotelCfarModelV1RefundableRoomUpdatedCopy,
    isFintechHotelUpdatedUxEnabled = false,
    selectedLodging,
    roomInfoProducts,
    selectRoomType,
    chosenRoomInfoIndex,
    ftcPricingUpdateVariant,
  } = props;
  const isFirstUpdate = useRef<boolean>(true);

  const [isOpenRoomPaymentScheduleModal, setOpenRoomPaymentScheduleModal] =
    useState(false);

  const [modalRoomInfoProducts, setModalRoomInfoProducts] = useState<
    undefined | RoomInfoProducts
  >(undefined);

  useEffect(() => {
    if (checkinDate && checkoutDate && (adultsCount || childrenCount)) {
      // skip the first update
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
      } else {
        fetchPremierCollectionShop(history, {
          forceCallHotelAvailability: true,
        });
      }
    }
  }, [checkinDate, checkoutDate, adultsCount, childrenCount]);

  return (
    <>
      <HotelShopRoomTypePickerRedesign
        {...props}
        roomInfoProductsType={
          roomInfoProductsType ?? {
            roomInfoProducts,
            variant: HotelShopRoomTypePickerEnum.Default,
          }
        }
        onClickContinue={(
          productTrackingProperties?: object,
          selectedRoomInfo?: RoomInfoProducts
        ) => {
          trackEvent({
            eventName: SELECT_PC_ROOM,
            properties: { ...(productTrackingProperties ?? {}) },
          });

          const shouldOpenPaymentScheduleModal = () => {
            const hasPayLater = selectedRoomInfo?.products.some(
              (product) =>
                product.roomPaymentSchedule === RoomPaymentSchedule.PayLater
            );

            return selectedLodging && hasPayLater;
          };

          const openPaymentScheduleModal = () => {
            const paymentSchedules = new Set(
              selectedRoomInfo?.products.map(
                (p) => p.roomPaymentSchedule ?? RoomPaymentSchedule.PayNow
              )
            );

            trackEvent({
              eventName: VIEWED_PAYMENT_SCHEDULE_MODAL,
              properties: {
                payment_schedule_available: Array.from(paymentSchedules)
                  .sort()
                  .join(),
                stage: "details_page",
              },
            });

            setModalRoomInfoProducts(selectedRoomInfo);
            setOpenRoomPaymentScheduleModal(true);
          };

          if (shouldOpenPaymentScheduleModal()) {
            openPaymentScheduleModal();
          } else if (handleReadyToRedirect) {
            handleReadyToRedirect();
          }
        }}
        className={clsx("b2b", "pc-desktop-room-picker")}
        reserveButtonClassName="reserve-cta"
        desktopReserveButtonText={<span>{`Continue for `}</span>}
        isHotelCfarModelV1RefundableRoomUpdatedCopy={
          isHotelCfarModelV1RefundableRoomUpdatedCopy
        }
        variant={
          isFintechHotelUpdatedUxEnabled ? "refundable-room-ux" : "default"
        }
        compliancePricingVariant={
          ftcPricingUpdateVariant !== "control"
            ? ftcPricingUpdateVariant
            : undefined
        }
      />

      {modalRoomInfoProducts && (
        <HotelRoomPaymentScheduleModal
          openModal={isOpenRoomPaymentScheduleModal}
          onClose={() => {
            setOpenRoomPaymentScheduleModal(false);
            setModalRoomInfoProducts(undefined);
          }}
          onClickContinue={(selectedProductIndex: number) => {
            trackEvent({
              eventName: PAYMENT_SCHEDULE_SELECTED,
              properties: {
                payment_schedule:
                  modalRoomInfoProducts.products[selectedProductIndex]
                    .roomPaymentSchedule ?? RoomPaymentSchedule.PayNow,
                stage: "details_page",
              },
            });
            selectRoomType(chosenRoomInfoIndex, selectedProductIndex);
            if (handleReadyToRedirect) {
              handleReadyToRedirect();
            }
          }}
          roomInfoProducts={modalRoomInfoProducts}
          isMobile={false}
        />
      )}
    </>
  );
};
